<template>
    <div class="enquiries-widget" :class="{ 'modal-context': isModal }">
        <!-- Content Area -->
        <div class="content-area with-toggle">
            <!-- AI FAQ Mode -->
            <div v-if="mode === 'ai'" class="ai-mode">
                <!-- Customer Service Agent -->
                <customer-service-agent
                    :occasion-id="occasionId"
                    :participant-id="participantId"
                    :show-metadata="showMetadata"
                    :max-questions="maxQuestions"
                    :api-endpoint="apiEndpoint"
                    :logo-url="logoUrl"
                    :logo-alt="logoAlt"
                    :welcome-message="welcomeMessage"
                    @question-submitted="$emit('question-submitted', $event)"
                    @response-received="$emit('response-received', $event)"
                    @error-occurred="$emit('error-occurred', $event)"
                    @rate-limit-exceeded="$emit('rate-limit-exceeded', $event)"
                    class="customer-service-clean"
                />
            </div>

            <!-- FAQ List Mode -->
            <div v-if="mode === 'faq-list'" class="faq-list-mode">
                <faqs-list-with-search
                    :occasion-id="occasionId"
                    :endpoint="faqEndpoint"
                    :show-search="showFaqSearch"
                    :id-extension="idExtension"
                    @faq-opened="handleFaqOpened"
                    @faq-closed="handleFaqClosed"
                >
                    <template #empty-actions>
                        <button
                            class="btn btn-sm btn-outline-primary"
                            @click="switchToContact"
                        >
                            Contact Us Instead
                        </button>
                    </template>
                </faqs-list-with-search>
            </div>

            <!-- Traditional Contact Mode -->
            <div v-if="mode === 'contact'" class="contact-mode">
                <enquiries-form
                    :occasion-id="occasionId"
                    @contact-submitted="$emit('contact-submitted', $event)"
                    @contact-success="$emit('contact-success', $event)"
                    @contact-error="$emit('contact-error', $event)"
                />
            </div>
        </div>

        <!-- Sticky Mode Toggle at Bottom -->
        <div class="mode-toggle-sticky">
            <div class="mode-tabs" role="group">
                <div
                    v-if="aiEnabled"
                    class="mode-tab"
                    :class="{ 'active': mode === 'ai' }"
                    @click="mode = 'ai'"
                    role="button"
                    tabindex="0"
                    @keydown.enter="mode = 'ai'"
                    @keydown.space.prevent="mode = 'ai'"
                >
<!--                    <i class="fas fa-robot"></i>-->
                    <span class="tab-text">Ask Us</span>
                </div>
                <div
                    v-if="faqListEnabled"
                    class="mode-tab"
                    :class="{ 'active': mode === 'faq-list' }"
                    @click="switchToFaqList"
                    role="button"
                    tabindex="0"
                    @keydown.enter="switchToFaqList"
                    @keydown.space.prevent="switchToFaqList"
                >
<!--                    <i class="fas fa-list"></i>-->
                    <span class="tab-text">FAQs</span>
                </div>
                <div
                    class="mode-tab"
                    :class="{ 'active': mode === 'contact' }"
                    @click="mode = 'contact'"
                    role="button"
                    tabindex="0"
                    @keydown.enter="mode = 'contact'"
                    @keydown.space.prevent="mode = 'contact'"
                >
<!--                    <i class="fas fa-envelope"></i>-->
                    <span class="tab-text">Contact</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomerServiceAgent from '../AiAgents/CustomerService/CustomerServiceAgent.vue'
import EnquiriesForm from '../Enquiries/EnquiriesForm.vue'
import FaqsListWithSearch from '../Faqs/ListWithSearch.vue'

export default {
    name: 'Messenger',

    components: {
        CustomerServiceAgent,
        EnquiriesForm,
        FaqsListWithSearch
    },

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        },
        defaultMode: {
            type: String,
            default: 'ai',
            validator: value => ['ai', 'faq-list', 'contact'].includes(value)
        },
        aiEnabled: {
            type: Boolean,
            default: true
        },
        faqListEnabled: {
            type: Boolean,
            default: true
        },
        showTitle: {
            type: Boolean,
            default: true
        },
        showFaqSearch: {
            type: Boolean,
            default: true
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 5
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },
        faqEndpoint: {
            type: String,
            default: null
        },
        idExtension: {
            type: String,
            default: 'modal'
        },
        participantId: {
            type: [Number, String],
            default: null
        },
        isModal: {
            type: Boolean,
            default: false
        },
        logoUrl: {
            type: String,
            default: null
        },
        logoAlt: {
            type: String,
            default: 'Logo'
        },
        welcomeMessage: {
            type: String,
            default: 'If you have any questions, you can ask me'
        }
    },

    data() {
        return {
            mode: this.defaultMode,
            showContactForm: true
        }
    },


    methods: {
        switchToAi() {
            this.mode = 'ai'
        },

        switchToFaqList() {
            this.mode = 'faq-list'
        },

        switchToContact() {
            this.mode = 'contact'
        },


        determineDefaultMode() {
            // Priority: AI if enabled, then FAQ list if enabled, then contact
            if (this.aiEnabled) return 'ai'
            if (this.faqListEnabled) return 'faq-list'
            return 'contact'
        },

        handleFaqOpened(faq) {
            this.$emit('faq-opened', faq)
        },

        handleFaqClosed() {
            this.$emit('faq-closed')
        }
    },

    mounted() {
        // If defaultMode is not available due to configuration, determine the best mode
        if ((this.defaultMode === 'ai' && !this.aiEnabled) ||
            (this.defaultMode === 'faq-list' && !this.faqListEnabled)) {
            this.mode = this.determineDefaultMode()
        }
    },

    watch: {
        defaultMode: {
            immediate: true,
            handler(newMode) {
                // Validate the mode is available based on configuration
                if ((newMode === 'ai' && !this.aiEnabled) ||
                    (newMode === 'faq-list' && !this.faqListEnabled)) {
                    this.mode = this.determineDefaultMode()
                } else {
                    this.mode = newMode
                }
            }
        },
        mode: {
            immediate: true,
            handler(newMode) {
                this.$emit('mode-changed', newMode)
            }
        }
    }
}
</script>

<style scoped>
.enquiries-widget {
    position: relative;
}

/* Modal-specific layout */
.enquiries-widget.modal-context {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* Content area - normal behavior by default */
.content-area {
    padding-bottom: 0;
}

/* Modal-specific content area behavior */
.enquiries-widget.modal-context .content-area {
    flex: 1;
    overflow-y: auto;
}

.enquiries-widget.modal-context .content-area.with-toggle {
    padding-bottom: 35px; /* Space for sticky buttons */
}

/* Sticky mode toggle - only sticky in modal context */
.mode-toggle-sticky {
    border-top: 1px solid #e9ecef;
    background: white;
}

/* Modal-specific sticky positioning */
.enquiries-widget.modal-context .mode-toggle-sticky {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    z-index: 1000; /* Lower z-index so footer border shows above */
}

/* Mode tabs styling */
.mode-tabs {
    display: flex;
    width: 100%;
}

.mode-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #6c757d;
    background: transparent;
    border: none;
    outline: none;
}

.mode-tab:hover {
    color: #007bff;
    background-color: rgba(0, 123, 255, 0.05);
}

.mode-tab.active {
    color: #007bff;
    background-color: rgba(0, 123, 255, 0.1);
}

.mode-tab:focus {
    outline: 2px solid #007bff;
    outline-offset: -2px;
}

.mode-tab i {
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
    transition: color 0.2s ease;
}

.mode-tab .tab-text {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1;
    transition: color 0.2s ease;
}

.contact-header h5 {
    color: #495057;
}

.contact-header i {
    margin-right: 0.5rem;
}

/* AI Mode needs full height */
.ai-mode {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Remove extra styling from Customer Service Agent when embedded */
.customer-service-clean :deep(.card) {
    border: none;
    box-shadow: none;
}

.customer-service-clean :deep(.card-header) {
    display: none;
}

.customer-service-clean :deep(.card-body) {
    padding: 0;
}

/* Form styling */
.form-group label {
    font-weight: 500;
    color: #495057;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .enquiries-widget.modal-context .mode-toggle-sticky {
        margin-left: -1.5rem;
        margin-right: -1.5rem;
        bottom: 0;
    }

    .enquiries-widget.modal-context .content-area.with-toggle {
        padding-bottom: 55px; /* Space for buttons on mobile */
    }

    .d-flex {
        flex-direction: column;
        gap: 0.75rem;
    }

    .d-flex .btn {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Messenger/Messenger.vue